/**
 * DEV ONLY. A local OIDC identity provider: an Ed25519 key (WebCrypto, no jose in
 * the bundle) signs EdDSA id tokens in the standard JWS compact format, and
 * jwks() publishes the matching public key. Point the API at it with
 * IDP_<P>_AUDIENCE / IDP_<P>_ISSUER / IDP_<P>_JWKS (see `pnpm --filter @mp/adapters dev-jwks`).
 *
 * The default key is derived from the published DEV_IDP_SEED so a dev API can pin
 * the JWKS once; it is not a secret and must never be trusted outside development.
 */
import type { KV } from '../storage.js';
import { b64url, hexBytes, utf8 } from './b64.js';
import type { IdentityName, IdentityProvider, IdentityToken } from './types.js';

export const DEV_IDP_ISSUER = 'https://dev-idp.market-pop.invalid';
export const DEV_IDP_AUDIENCE = 'market-pop-dev';
export const DEV_IDP_SEED = '6d61726b65742d706f702d6465762d6964702d6b65792d6e6f742d7365637265';

/** PKCS#8 wrapper for a raw 32-byte Ed25519 seed (RFC 8410). */
const PKCS8_ED25519_PREFIX = '302e020100300506032b657004220420';

export interface PublicJwk {
  kty: 'OKP';
  crv: 'Ed25519';
  x: string;
  kid: string;
  alg: 'EdDSA';
  use: 'sig';
}

export class DevIdentityKey {
  private constructor(
    private readonly key: CryptoKey,
    readonly jwk: PublicJwk,
  ) {}

  /** Seed as 64 hex chars. */
  static async fromSeed(seedHex: string = DEV_IDP_SEED): Promise<DevIdentityKey> {
    const seed = hexBytes(seedHex);
    if (seed.length !== 32) throw new Error('Ed25519 seed must be 32 bytes');
    const der = hexBytes(PKCS8_ED25519_PREFIX + seedHex);
    const key = await crypto.subtle.importKey('pkcs8', der, { name: 'Ed25519' }, true, ['sign']);
    const { x } = (await crypto.subtle.exportKey('jwk', key)) as { x?: string };
    if (!x) throw new Error('Ed25519 export has no public key');
    return new DevIdentityKey(key, { kty: 'OKP', crv: 'Ed25519', x, kid: await thumbprint(x), alg: 'EdDSA', use: 'sig' });
  }

  /** A per-device key kept in `kv` (for testing key rotation or isolated devices). */
  static async persisted(kv: KV, key = 'devIdp.seed'): Promise<DevIdentityKey> {
    let seed = kv.get(key);
    if (!seed) {
      seed = Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');
      kv.set(key, seed);
    }
    return DevIdentityKey.fromSeed(seed);
  }

  jwks(): { keys: PublicJwk[] } {
    return { keys: [this.jwk] };
  }

  /** JWS compact serialization, header {alg: EdDSA, kid, typ: JWT}. */
  async sign(claims: Record<string, unknown>): Promise<string> {
    const header = b64url(utf8(JSON.stringify({ alg: 'EdDSA', kid: this.jwk.kid, typ: 'JWT' })));
    const payload = b64url(utf8(JSON.stringify(claims)));
    const input = `${header}.${payload}`;
    const sig = await crypto.subtle.sign({ name: 'Ed25519' }, this.key, utf8(input));
    return `${input}.${b64url(new Uint8Array(sig))}`;
  }
}

/** RFC 7638 JWK thumbprint of an Ed25519 public key. */
async function thumbprint(x: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', utf8(JSON.stringify({ crv: 'Ed25519', kty: 'OKP', x })));
  return b64url(new Uint8Array(digest));
}

export interface DevIdentityOptions {
  /** defaults to the shared DEV_IDP_SEED key, created on first sign-in */
  key?: () => Promise<DevIdentityKey>;
  issuer?: string;
  audience?: string;
  /** stable subject; defaults to a random id kept in `kv` per provider */
  subject?: string;
  kv?: KV;
  email?: string;
  ttlSec?: number;
  now?: () => number;
}

let sharedKey: Promise<DevIdentityKey> | null = null;
export const devIdentityKey = (): Promise<DevIdentityKey> => (sharedKey ??= DevIdentityKey.fromSeed());

export class DevIdentityProvider implements IdentityProvider {
  constructor(
    readonly provider: IdentityName,
    private readonly o: DevIdentityOptions = {},
  ) {}

  subject(): string {
    if (this.o.subject) return this.o.subject;
    const k = `devIdp.sub.${this.provider}`;
    let sub = this.o.kv?.get(k) ?? null;
    if (!sub) {
      sub = `dev-${this.provider}-${crypto.randomUUID()}`;
      this.o.kv?.set(k, sub);
    }
    return sub;
  }

  async signIn(): Promise<IdentityToken> {
    const iat = Math.floor((this.o.now ?? Date.now)() / 1000);
    const sub = this.subject();
    const key = await (this.o.key ?? devIdentityKey)();
    const idToken = await key.sign({
      iss: this.o.issuer ?? DEV_IDP_ISSUER,
      aud: this.o.audience ?? DEV_IDP_AUDIENCE,
      sub,
      iat,
      exp: iat + (this.o.ttlSec ?? 600),
      email: this.o.email ?? `${sub}@dev.market-pop.invalid`,
      email_verified: true,
    });
    return { provider: this.provider, idToken };
  }
}
